import { useMemo, useRef, useState } from "react";
import {
  getAthletesByCategory,
  getAthletesByCurrentLiftWeight,
  getCurrentLift,
  getStatusField,
} from "../../../util/src/index";
import { AthletesModel, GroupModel, LiftStatus } from "../../../util/src/types";
import { CategoryColors, CollapsedCategories, GroupedAthletes } from "./types";

/**
 * Hook for managing sorted athletes based on sorting preference
 */
export const useSortedAthletes = (
  currentGroup: GroupModel | null,
  sortByLiveWeight: boolean,
) => {
  return useMemo(() => {
    if (sortByLiveWeight) {
      return getAthletesByCurrentLiftWeight(
        currentGroup?.athletes ? [...currentGroup.athletes] : [],
        getCurrentLift(currentGroup?.athletes),
      );
    } else {
      return getAthletesByCategory(
        currentGroup?.athletes ? [...currentGroup.athletes] : [],
      );
    }
  }, [currentGroup?.athletes, sortByLiveWeight]);
};

/**
 * Hook for managing current lift information
 */
export const useCurrentLift = (currentGroup: GroupModel | null) => {
  return useMemo(() => {
    const currentLift = getCurrentLift(currentGroup?.athletes);
    const currentLiftType = currentLift?.replace(/[123]$/, "") as
      | "squat"
      | "bench"
      | "deadlift";
    return { currentLift, currentLiftType };
  }, [currentGroup?.athletes]);
};

/**
 * Hook for generating category colors
 */
export const useCategoryColors = (
  sortedAthletes: AthletesModel[],
): CategoryColors => {
  return useMemo(() => {
    // Create category keys
    const categoryKeys = sortedAthletes.map(
      (a) =>
        `${a.catAge?.nom || ""}|${a.catPoid?.nom || ""}|${a.discipline?.code || ""}`,
    );

    const uniqueCategories = Array.from(new Set(categoryKeys)).filter(
      (category) => Boolean(category) && category !== "|",
    );

    const colors = [
      "rgb(255, 235, 242)", // Rose pastel très clair
      "rgb(230, 248, 255)", // Bleu pastel très clair
      "rgb(230, 255, 230)", // Vert pastel très clair
      "rgb(255, 245, 235)", // Pêche pastel très clair
      "rgb(250, 238, 250)", // Violet pastel très clair
      "rgb(255, 250, 242)", // Bisque pastel très clair
      "rgb(238, 252, 255)", // Turquoise pastel très clair
      "rgb(255, 242, 248)", // Rose clair pastel très clair
    ];

    const colorMap = Object.fromEntries(
      uniqueCategories.map((category, index) => [
        category,
        colors[index % colors.length],
      ]),
    ) as CategoryColors;

    return colorMap;
  }, [sortedAthletes]);
};

/**
 * Hook for managing collapsed categories state
 */
export const useCollapsedCategories = () => {
  const [collapsedCategories, setCollapsedCategories] =
    useState<CollapsedCategories>({});

  const toggleCategory = (categoryKey: string) => {
    setCollapsedCategories((prev) => ({
      ...prev,
      [categoryKey]: !prev[categoryKey],
    }));
  };

  return { collapsedCategories, toggleCategory };
};

/**
 * Hook for grouping athletes by category
 */
export const useGroupedAthletes = (
  sortedAthletes: AthletesModel[],
  sortByLiveWeight: boolean,
): GroupedAthletes => {
  return useMemo(() => {
    if (sortByLiveWeight) {
      // In live weight mode, don't create groups
      return {
        all: sortedAthletes,
      };
    } else {
      // In category mode, group athletes
      return sortedAthletes.reduce((groups: GroupedAthletes, athlete) => {
        const categoryKey = `${athlete.catAge?.nom || ""}|${athlete.catPoid?.nom || ""}`;
        if (!groups[categoryKey]) {
          groups[categoryKey] = [];
        }
        groups[categoryKey].push(athlete);
        return groups;
      }, {});
    }
  }, [sortedAthletes, sortByLiveWeight]);
};

/**
 * Hook for table scroll management
 * Simplified to only provide ref without unnecessary scroll listeners
 */
export const useTableScroll = () => {
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Remove scroll listener as sticky positioning handles frozen cells automatically
  // and empty scroll handlers can cause performance issues

  return tableContainerRef;
};

/**
 * Utility function to get maximum lift for an athlete
 */
export const getMaxLift = (athlete: AthletesModel, liftType: string) => {
  const lifts = [1, 2, 3].map((n) => {
    const liftField = `${liftType}${n}` as keyof AthletesModel;
    const statusField = getStatusField(liftField);
    if (
      athlete[statusField] === LiftStatus.LIFT ||
      athlete[statusField] === LiftStatus.ANNONCED
    ) {
      return { lift: athlete[liftField] as number, field: liftField };
    }
    return { lift: 0, field: statusField };
  });
  return (
    lifts
      .filter((lift) => lift.lift !== 0)
      .sort((a, b) => b.lift - a.lift)[0] || { lift: 0, field: "" }
  );
};
